<script setup lang="ts">
  import type { AIChatSession, CustomerCase, CustomerOrganisationUser } from '@/types/generated'
  import { computed, inject, type PropType, ref, type Ref } from 'vue'
  import UserAvatar from '@/components/app/users/UserAvatar.vue'
  import { useTranslation } from '@/composables/useTranslation'

  const props = defineProps({
    chatSession: {
      type: Object as PropType<AIChatSession>,
      required: true
    }
  })

  const { t } = useTranslation({
    en: {
      EmptyMessage: 'No messages'
    },
    de: {
      EmptyMessage: 'Keine Nachrichten'
    }
  })

  const activeChatSession = inject<Ref<AIChatSession | null>>('activeChatSession', ref(null))
  const isSessionGenerating = inject<(uuid: string) => boolean>('isSessionGenerating', () => false)
  const active = computed(() => activeChatSession.value?.uuid === props.chatSession.uuid)
  const caseData = inject<Ref<CustomerCase | null>>('caseData', ref(null))
  const user = computed<CustomerOrganisationUser | null>(() => {
    return (
      caseData.value?.org.users.find((user) => user.uuid === props.chatSession.started_by_user) ||
      null
    )
  })
  const lastUserMessage = computed(() => {
    const userMessages = props.chatSession.messages
      .filter((msg) => msg.role === 'user')
      .sort((a, b) => new Date(b.created_at || '') - new Date(a.created_at || ''))
    return userMessages[0]?.content || t('EmptyMessage')
  })

  const truncatedLastMessage = computed(() => {
    const msg = lastUserMessage.value
    return msg.length > 50 ? msg.slice(0, 50) + '...' : msg
  })
</script>
<template>
  <div
    v-if="user"
    class="relative cursor-pointer hover:bg-gray-100 focus:bg-gray-100 focus:outline-none
      dark:border-neutral-700 dark:bg-neutral-800 dark:hover:bg-neutral-700 dark:focus:bg-neutral-700"
    :class="active ? 'bg-gray-200' : 'bg-white'"
  >
    <div
      class="flex items-center gap-x-3 border-b border-b-gray-100 px-5 py-4 dark:border-neutral-700"
    >
      <div class="shrink-0">
        <div class="relative size-8">
          <UserAvatar
            v-if="user"
            :user="user"
            size="32px"
          />
        </div>
      </div>
      <div class="grow truncate">
        <div class="flex items-center justify-between gap-x-1">
          <p class="truncate text-[13px] font-medium text-gray-800 dark:text-neutral-200">
            {{ user.first_name }} {{ user.last_name }}
          </p>
          <div>
            <div class="flex items-center gap-2">
              <span
                v-if="isSessionGenerating(chatSession.uuid)"
                class="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500"
              ></span>
              <span class="text-[10px] uppercase text-gray-400 dark:text-neutral-500">
                {{ new Date(chatSession.created_at).toLocaleDateString('de-DE') }}
              </span>
            </div>
          </div>
        </div>
        <div class="me-5 truncate">
          <p class="truncate text-xs text-gray-500 dark:text-neutral-500">
            {{ truncatedLastMessage }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
